import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {isEmpty} from "weed-js";
import {enqueueSnackbar} from "../../redux/notifications/Action";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import {Autocomplete, Grid} from "@mui/material";
import TextInput from "../elements/TextInput";
import {Client} from "weed-js";
import "./SaveReportView.css";
import {
    View as ReportView,
} from "../../models/sso.js";


export const SaveReportView = ({currentView, loggedInUser, selectedIntegration, selectedOrganization, verticals, categories, setNewViews}) => {

    const [errors, setErrors] = useState({});
    const dispatch = useDispatch();
    const client = new Client();
    const [verticalName, setVerticalName] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [open, setOpen] = useState(false);

    const [formState, setFormState] = useState({
        vertical_id: null,
        category_id: null,
        description: null,
        name: null,
        created_by_id: loggedInUser?.user?.id,
        organization_id: selectedOrganization?.id,
        integration: selectedIntegration,
    });

    const saveView = (v) => {
        if (!formState?.vertical_id) {
            setErrors(prev => ({...prev, vertical_id: "Please select a vertical"}));
            return;
        }
        if (!formState?.category_id) {
            setErrors(prev => ({...prev, category_id: "Please select a category"}));
            return;
        }
        if (!formState?.name || formState?.name.trim().length === 0) {
            setErrors(prev => ({...prev, name: "Please enter a name for the report"}));
            return;
        }
        if (!currentView?.canSave()) {
            dispatch(enqueueSnackbar({message: "No changes to save in the report", options: {variant: "warning"}}));
            return;
        }
        if (isEmpty(v.toJSON())) {
            dispatch(enqueueSnackbar({message: "Cannot save an empty report", options: {variant: "error"}}));
            return;
        }

        const reportView = new ReportView({
            id: null,
            integration: selectedIntegration,
            organization_id: selectedOrganization?.id,
            vertical_id: formState.vertical_id,
            category_id: formState.category_id,
            name: formState.name,
            description: formState.description,
            created_by_id: loggedInUser?.user?.id,
            json: v.toJSON(),
        });

        reportView.createOrUpdate(client).then((createdView) => {
            dispatch(enqueueSnackbar({message: "Report saved successfully", options: {variant: "success"}}));
            createdView.vertical = verticals.find(v => v.id === createdView.vertical_id) || null;
            createdView.category = categories.find(c => c.id === createdView.category_id) || null
            setNewViews(prev => {
                return [...prev.filter(v => v.id !== createdView.id), createdView];
            });
            setOpen(false);
        }).catch(err => {
            console.error("Error saving report view:", err);
            dispatch(enqueueSnackbar({message: "Error saving report: " + err.message, options: {variant: "error"}}));
        });
    }

    const baseVerticalOptions=(verticals || []).map(org => ({
        label: org.name,
        value: org.id
    }));

    const verticalOptions = (() => {
        const name = (verticalName || "").trim();
        if (!name) return baseVerticalOptions;
        const exists = verticals.find(v => v.name.toLowerCase() === name.toLowerCase());
        return exists ? baseVerticalOptions : [{label: name, value: null}, ...baseVerticalOptions];
    })();

    const baseCategoryOptions = (categories || []).map(org => ({
        label: org.name,
        value: org.id
    }));

    const categoryOptions = (() => {
        const name = (categoryName || "").trim();
        if (!name) return baseCategoryOptions;
        const exists = categories.filter(category => category.vertical_id === formState.vertical_id).find(v => v.name.toLowerCase() === name.toLowerCase());
        return exists ? baseCategoryOptions : [{label: name, value: null}, ...baseCategoryOptions];
    })();

    const nothingToSave = !currentView?.canSave();

    if (!open) {
        return <Button
            variant={"contained"}
            onClick={() => setOpen(true)}>Save Report</Button>;
    }

    return <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
    >
        <Box className={"save-report-modal"} sx={{maxWidth: 600}}>
            <h2 id="modal-modal-title">Save Report</h2>
            {nothingToSave && <p className={"error-text"}>You haven&apos;t made any changes to save.</p>}
            <div id="modal-modal-description">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <label htmlFor={"vertical"}>Vertical</label>
                        <Autocomplete
                            className={"save-report-choose-vertical"}
                            filterSelectedOptions
                            inputValue={verticalName}
                            id={"choose-vertical-dropdown"}
                            name={"choose-vertical-dropdown"}
                            role={"choose-vertical-dropdown"}
                            options={verticalOptions}
                            disablePortal={true}
                            disabled={nothingToSave}
                            isOptionEqualToValue={(option, value) => {
                                if (option?.value != null && value?.value != null) return option.value === value.value;
                                return (option?.label ?? "") === (value?.label ?? "");
                            }}
                            onChange={(event, option) => {
                                if (!option) {
                                    setVerticalName("");
                                    setFormState(fs => ({ ...fs, vertical_id: null }));
                                    return;
                                }
                                setVerticalName(option.label);
                                setFormState(fs => ({ ...fs, vertical_id: option.value || null}));
                                setErrors(prev => ({ ...prev, vertical_id: undefined }));
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === "input" || reason === "reset" || reason === "clear") {
                                    setVerticalName(newInputValue || "");
                                    // As soon as we’re typing, this is a "new" option => id=null
                                    setFormState(fs => ({ ...fs, vertical_id: null }));
                                    // Clear any stale error for vertical
                                    setErrors(prev => ({ ...prev, vertical_id: undefined }));
                                }
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === "string") {
                                    return option;
                                }
                                return option?.label ?? "";
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={`choose-vertical-${option.value ?? `new-${option.label}`}`}>
                                    {option.value == null && option.label?.trim()
                                        ? `Create “${option.label.trim()}”`
                                        : option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextInput
                                    {...params}
                                    name="vertical"
                                    label="Vertical"
                                    formState={formState}
                                    setFormState={setFormState}
                                    value={verticalName}
                                    errors={errors}
                                    setErrors={setErrors}
                                    disabled={nothingToSave}
                                    placeholder={"e.g. 'Retail'"}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <label htmlFor={"category"}>Category</label>
                        <Autocomplete
                            className={"save-report-choose-category"}
                            filterSelectedOptions
                            inputValue={categoryName}
                            id={"choose-category-dropdown"}
                            name={"choose-category-dropdown"}
                            role={"choose-category-dropdown"}
                            options={categoryOptions}
                            disablePortal={true}
                            disabled={nothingToSave}
                            isOptionEqualToValue={(option, value) => {
                                if (option?.value != null && value?.value != null) return option.value === value.value;
                                return (option?.label ?? "") === (value?.label ?? "");
                            }}
                            onChange={(event, option) => {
                                if (!option) {
                                    setCategoryName("");
                                    setFormState(fs => ({ ...fs, category_id: null }));
                                    return;
                                }
                                setCategoryName(option.label);
                                setFormState(fs => ({ ...fs, category_id: option.value || null}));
                                setErrors(prev => ({ ...prev, category_id: undefined }));
                            }}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === "input" || reason === "reset" || reason === "clear") {
                                    setCategoryName(newInputValue || "");
                                    // As soon as we’re typing, this is a "new" option => id=null
                                    setFormState(fs => ({ ...fs, category_id: null }));
                                    // Clear any stale error for category
                                    setErrors(prev => ({ ...prev, category_id: undefined }));
                                }
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === "string") {
                                    return option;
                                }
                                return option?.label ?? "";
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={`choose-category-${option.value ?? `new-${option.label}`}`}>
                                    {option.value == null && option.label?.trim()
                                        ? `Create “${option.label.trim()}”`
                                        : option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextInput
                                    {...params}
                                    name="category"
                                    label="Category"
                                    formState={formState}
                                    setFormState={setFormState}
                                    value={categoryName}
                                    errors={errors}
                                    setErrors={setErrors}
                                    disabled={nothingToSave}
                                    placeholder={"e.g. 'Procurement'"}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <label htmlFor={"name"}>Report Title/Name</label>
                        <TextInput
                            name={"name"}
                            label={"Report Name"}
                            disabled={nothingToSave}
                            formState={formState}
                            setFormState={setFormState}
                            value={formState?.name || ""}
                            errors={errors}
                            setErrors={setErrors}
                            placeholder={"e.g. 'My Report'"}
                            autoFocus={false}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <label htmlFor={"description"}>Description (optional)</label>
                        <TextInput
                            name={"description"}
                            label={"Description"}
                            formState={formState}
                            setFormState={setFormState}
                            disabled={nothingToSave}
                            value={formState?.description || ""}
                            errors={errors}
                            setErrors={setErrors}
                            placeholder={"A brief description of the report"}
                            multiline={true}
                            minRows={2}
                        />
                    </Grid>
                </Grid>
            </div>
            <div className={"save-report-actions"}>
                <Box sx={{display: "flex", flexDirection: "row", pt: 2}}>
                    <Box sx={{flex: "1 1 auto"}}/>
                    <Button
                        variant="contained"
                        disabled={nothingToSave || (!formState?.vertical_id && !verticalName) || (!formState?.category_id && !categoryName)|| !formState?.name || (formState?.name.trim().length === 0)}
                        onClick={() => saveView(currentView)}
                    >
                        Save
                    </Button>
                </Box>
            </div>
        </Box>
    </Modal>
}


export default SaveReportView;